/**
 *
 * Users
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { useParams } from 'react-router-dom';

import { flushState, loadUsers, updateUser } from './actions';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectUsersListFirstElem,
  makeSelectUsersList,
  makeSelectUser,
} from './selectors';

// antd component
import { Layout, Form } from 'antd';
const { Content } = Layout;

//components
import SideList from 'components/SideList';
import Preloader from 'components/Preloader';
import RenderPopconfirm from 'components/RenderPopconfirm';
// inner components
import UsersDetail from './containers/UsersDetail';

// init forms
import { initFormsByRole } from 'utils/forms/forms.init';
// helper for forms
import { setFormValues } from 'utils/forms/forms.utils';

export function Users({
  flushState,
  loading,
  history,
  userDetail,
  usersList,
  firstElem,
  // actions
  loadUsers,
  updateUser,
}) {
  useInjectReducer({ key: 'users', reducer });
  useInjectSaga({ key: 'users', saga });

  const newKey = 'nouvel';

  const { id: initId } = useParams();

  const initRole = 'parent';
  const initForm = initFormsByRole(initRole);
  // Form initialization for "general" tab
  const [customForm] = Form.useForm();

  const [generalForm, setGeneralForm] = useState(initForm);
  const [currentItem, setCurrentItem] = useState(initId);
  const [currentElement, setCurrentElement] = useState(null);
  const [formHaveChanges, setFormHaveChanges] = useState(false);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    !usersList && loadUsers();
    return () => {
      // Clearing the state after unmounting a component
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // Get first element from lists
  // Because history not trigger update component
  useEffect(() => {
    if (initId) {
      setCurrentItem(initId);
    }
  }, [initId, firstElem]);

  const changeCurrentItem = current => {
    setCurrentItem(current);
    history.push({
      pathname: `/utilisateurs/${current}`,
      search: '',
      hash: '',
      state: {},
    });
  };
  const addNewItem = () => {
    setCurrentItem(newKey);
    history.push({
      pathname: `/utilisateurs/${newKey}`,
    });
  };

  const resetForm = () => customForm.resetFields();
  const submitForm = () => customForm.submit();
  const setFormFieldsValues = values => {
    values && customForm.setFieldsValue(values);
  };
  // update data in the general form
  const setFormData = (formData, genForm = null) => {
    const values = formData || currentElement;
    const mainForm = genForm || generalForm;
    values && setFormValues(customForm, mainForm, values);
  };

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>Users</title>
        <meta name="description" content="Description of Users" />
      </Helmet>
      {loading && !!!usersList && <Preloader />}
      {!loading && !!usersList && (
        <>
          <RenderPopconfirm
            placement="right"
            showPopup={formHaveChanges}
            onConfirm={() => {
              submitForm();
              setFormHaveChanges(false);
            }}
            onCancel={() => {
              setFormHaveChanges(false);
              // update data in the general form
              setFormData();
            }}
          >
            <SideList
              className={formHaveChanges ? 'preventClass' : ''}
              type="utilisateurs"
              title="Utilisateurs"
            // subTitle={`${usersList?.totalCount || 0} utilisateurs`}
              sortProps={['description.lastname', 'description.firstname']}
              searchProps={[
                'description.firstname',
                'description.lastname',
                'role',
                'searchName',
              ]}
              filterProps={'role'}
              list={usersList?.data}
              currentItem={currentItem}
              currentItemAction={changeCurrentItem}
              addAction={() => addNewItem()}
            />
          </RenderPopconfirm>
          <Content className="main-content-container">
            <UsersDetail
              form={{
                customForm: customForm,
                resetForm: resetForm,
                submitForm: submitForm,
                setFormData: setFormData,
                setFormFieldsValues: setFormFieldsValues,
              }}
              generalForm={generalForm}
              setGeneralForm={setGeneralForm}
              currentId={currentItem}
              update={updateUser}
              updatedItem={userDetail}
              formHaveChanges={formHaveChanges}
              setFormHaveChanges={setFormHaveChanges}
              currentElement={currentElement}
              setCurrentElement={setCurrentElement}
            />
          </Content>
        </>
      )}
    </Layout>
  );
}

Users.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  usersList: makeSelectUsersList(),
  userDetail: makeSelectUser(),
  firstElem: makeSelectUsersListFirstElem(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadUsers: () => dispatch(loadUsers()),
    updateUser: (userData, actionType = 'update') => dispatch(updateUser(userData, actionType)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Users);
